import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, Cell, ReferenceLine,
} from 'recharts';
import '../styles/fmp.css';
import logoComece from '../assets/logo-comece.svg';

const NAVY = '#00205b';
const CYAN = '#06b6d4';
const GOLD = '#d4af37';
const RED = '#dc3545';
const GREEN = '#1fa971';
const GRAY = '#e3e6ea';

const fmtBRL = (v) =>
  (v === null || v === undefined) ? '—'
    : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 });

const fmtBRL2 = (v) =>
  (v === null || v === undefined) ? '—'
    : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 });

const fmtCompacto = (v) => {
  const n = Number(v) || 0;
  const abs = Math.abs(n);
  if (abs >= 1000000) return `${(n / 1000000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}M`;
  if (abs >= 1000) return `${(n / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}k`;
  return n.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
};

const MESES_PT = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const rotuloMes = (m) => {
  const [a, mm] = String(m).split('-');
  return `${MESES_PT[parseInt(mm, 10) - 1]}/${a.slice(2)}`;
};

// Tooltip padrão FMP: fundo preto, texto claro (itemStyle força a cor clara —
// sem ele o Recharts pinta o texto com a cor da série, ilegível no fundo escuro)
const tooltipDark = {
  backgroundColor: '#0e1726', border: 'none', borderRadius: 8,
  color: '#e2e8f0', fontSize: 14, padding: '10px 14px',
};
const tooltipItem = { color: '#e2e8f0' };
const tooltipLabel = { color: '#94a3b8', fontWeight: 600, marginBottom: 4 };

// Ícone com explicação da métrica ao passar o mouse (padrão em todos os cards)
const Info = ({ texto }) => {
  const [aberto, setAberto] = useState(false);
  return (
    <span
      style={{ position: 'relative', display: 'inline-block', marginLeft: 6, verticalAlign: 'middle' }}
      onMouseEnter={() => setAberto(true)}
      onMouseLeave={() => setAberto(false)}
    >
      <i className="fas fa-circle-info" style={{ color: '#9db2c9', fontSize: 13, cursor: 'help' }}></i>
      {aberto && (
        <span style={{
          position: 'absolute', bottom: '140%', left: '50%', transform: 'translateX(-50%)',
          background: '#0e1726', color: '#e2e8f0', padding: '10px 12px', borderRadius: 8,
          fontSize: 13, fontWeight: 400, width: 300, zIndex: 2000, textAlign: 'left',
          whiteSpace: 'normal', lineHeight: 1.5, boxShadow: '0 6px 18px rgba(0,0,0,.3)',
        }}>
          {texto}
        </span>
      )}
    </span>
  );
};

const RelatorioInvestidores = () => {
  const { clienteId } = useParams();
  const navigate = useNavigate();
  const [dados, setDados] = useState(null);
  const [cliente, setCliente] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [anonimizar, setAnonimizar] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const token = localStorage.getItem('access');
      const auth = { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 };
      const [rMetricas, rCliente] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_BASE}/api/cliente-bpo/${clienteId}/investidores/`, auth),
        axios.get(`${process.env.REACT_APP_API_BASE}/api/cliente-bpo/${clienteId}/`, auth).catch(() => null),
      ]);
      if (rMetricas.data?.success) setDados(rMetricas.data);
      else setErro(rMetricas.data?.erro || 'Erro ao carregar as métricas');
      if (rCliente?.data?.success) setCliente(rCliente.data.cliente);
    } catch (e) {
      setErro(e.response?.data?.erro || 'Erro ao conectar com o servidor');
    } finally {
      setCarregando(false);
    }
  }, [clienteId]);

  useEffect(() => { carregar(); }, [carregar]);

  if (carregando) {
    return (
      <div className="das-page fmp-dash-page">
        <div className="container-fluid mt-5 text-center py-5">
          <div className="spinner-border" style={{ color: NAVY }}></div>
          <p className="mt-3 text-muted">Calculando métricas do relatório...</p>
        </div>
      </div>
    );
  }
  if (erro || !dados) {
    return (
      <div className="das-page fmp-dash-page">
        <div className="container-fluid mt-5">
          <div className="alert alert-danger">Erro: {erro || 'sem dados'}</div>
          <button className="btn btn-outline-dark" onClick={carregar}>Tentar de novo</button>
        </div>
      </div>
    );
  }

  const { receita, concentracao, ebitda, caixa, recebiveis, falta_faturar,
          despesas, caixa_evolucao, base_clientes, orcamento,
          unit_economics, projetos } = dados;

  // Base de receita escolhida automaticamente pelo backend:
  // 'nfse' = notas emitidas | 'titulos' = títulos emitidos (cliente não emite NFS-e p/ tudo)
  const baseTitulos = dados.base_receita === 'titulos';
  const labelReceita = baseTitulos ? 'Receita (títulos)' : 'Receita (NFS-e)';
  const labelUnidade = baseTitulos ? 'título(s)' : 'nota(s)';

  const serieReceita = receita.serie.map((x) => ({ ...x, rotulo: rotuloMes(x.mes) }));
  const serieEbitda = ebitda.serie.map((x) => ({ ...x, rotulo: rotuloMes(x.mes) }));
  const serieFluxo = caixa.fluxo.map((x) => ({
    ...x, rotulo: rotuloMes(x.mes), saidasAbs: Math.abs(x.saidas),
  }));

  const nomeAnonimo = (i) => `Cliente ${String.fromCharCode(65 + i)}`;
  const topClientes = concentracao.top.map((c, i) => ({
    ...c, rotulo: anonimizar ? nomeAnonimo(i) : (c.cliente.length > 32 ? c.cliente.slice(0, 32) + '…' : c.cliente),
  }));

  // Evolução do caixa (realizado) + projeção 6 meses (lançamentos previstos)
  const serieEvolucaoCaixa = (() => {
    const hist = (caixa_evolucao || []).map((x) => ({ mes: x.mes, rotulo: rotuloMes(x.mes), saldo: x.saldo }));
    let proj = (dados.caixa_projecao || []).map((x) => ({
      mes: x.mes, rotulo: rotuloMes(x.mes), projetado: x.saldo_projetado,
      entradasPrev: x.entradas, saidasPrev: x.saidas,
    }));
    if (hist.length && proj.length) {
      const ultimo = hist[hist.length - 1];
      if (proj[0].mes === ultimo.mes) {
        // mês atual: mostra o saldo de hoje e a projeção do fim do mês no mesmo ponto
        hist[hist.length - 1] = { ...ultimo, projetado: proj[0].projetado };
        proj = proj.slice(1);
      } else {
        // conecta a linha projetada ao último ponto realizado
        hist[hist.length - 1] = { ...ultimo, projetado: ultimo.saldo };
      }
    }
    return [...hist, ...proj];
  })();
  const temProjecao = (dados.caixa_projecao || []).length > 0;
  const ultimaProjecao = temProjecao ? dados.caixa_projecao[dados.caixa_projecao.length - 1] : null;
  const topFornecedores = (despesas?.top_fornecedores || []).map((f, i) => ({
    ...f,
    rotulo: anonimizar
      ? `Fornecedor ${String.fromCharCode(65 + i)}`
      : (String(f.fornecedor).length > 32 ? String(f.fornecedor).slice(0, 32) + '…' : f.fornecedor),
  }));
  const baseDet = base_clientes?.detalhe || {};
  const ltvTop = (unit_economics?.ltv_top || []).map((c, i) => ({
    ...c,
    rotulo: anonimizar ? nomeAnonimo(i)
      : (String(c.cliente).length > 30 ? String(c.cliente).slice(0, 30) + '…' : c.cliente),
  }));
  const retencao = unit_economics?.retencao || [];
  const topProjetos = projetos?.top || [];
  // Só mostra a seção de projetos se o cliente realmente controla projetos:
  // pelo menos 1% das entradas com projeto vinculado E algum projeto com receita
  const clienteControlaProjetos =
    (projetos?.cobertura_receita_pct || 0) >= 1 && topProjetos.some((p) => p.receita > 0);

  const aging = recebiveis.aging || {};
  const faixasAging = [
    { k: 'a_vencer', rotulo: 'A vencer', cor: NAVY },
    { k: 'v30', rotulo: '1–30 dias', cor: GOLD },
    { k: 'v60', rotulo: '31–60 dias', cor: '#e67e22' },
    { k: 'v90', rotulo: '61–90 dias', cor: RED },
    { k: 'v90mais', rotulo: '+90 dias', cor: '#8b0000' },
  ];

  const yoyBadge = (v) => v === null || v === undefined ? null : (
    <span className={`badge ${v >= 0 ? 'bg-success' : 'bg-danger'} ms-2`} style={{ fontSize: 12 }}>
      {v >= 0 ? '▲' : '▼'} {Math.abs(v).toLocaleString('pt-BR')}% YoY
    </span>
  );

  const highlights = [
    {
      titulo: `Receita 12 meses (${rotuloMes(receita.periodo_ltm.inicio)}–${rotuloMes(receita.periodo_ltm.fim)})`,
      valor: fmtBRL(receita.ltm), extra: yoyBadge(receita.yoy_ltm),
      sub: `ano anterior: ${fmtBRL(receita.ltm_anterior)}`, icone: 'fa-chart-line', cor: NAVY,
      info: 'Soma de todas as notas fiscais de serviço emitidas nos últimos 12 meses fechados (o mês atual, ainda incompleto, fica fora). O selo YoY compara com os 12 meses anteriores.',
    },
    {
      titulo: `Receita ${new Date().getFullYear()} (YTD)`,
      valor: fmtBRL(receita.ytd), extra: yoyBadge(receita.yoy_ytd),
      sub: `mesmo período ${new Date().getFullYear() - 1}: ${fmtBRL(receita.ytd_anterior)}`,
      icone: 'fa-calendar-check', cor: CYAN,
      info: 'Receita do ano atual, de janeiro até hoje (YTD = year to date), comparada com exatamente o mesmo período do ano passado.',
    },
    {
      titulo: 'EBITDA gerencial 12m',
      valor: fmtBRL(ebitda.ltm),
      extra: ebitda.margem_ltm !== null && (
        <span className="badge bg-light text-dark border ms-2" style={{ fontSize: 12 }}>
          margem {ebitda.margem_ltm.toLocaleString('pt-BR')}%
        </span>
      ),
      sub: 'receita − impostos − custos − despesas op.', icone: 'fa-scale-balanced', cor: GOLD,
      info: 'Lucro operacional aproximado dos últimos 12 meses: receita menos impostos, custos e despesas operacionais (despesas financeiras ficam fora). A margem indica quanto de cada real de receita vira resultado.',
    },
    {
      titulo: 'Caixa hoje',
      valor: fmtBRL(caixa.total),
      sub: `${caixa.contas.length} contas (extrato conciliado)`, icone: 'fa-vault', cor: GREEN,
      info: 'Dinheiro disponível agora: soma do último saldo conciliado de todas as contas bancárias e aplicações no extrato.',
    },
    {
      titulo: 'Runway',
      valor: caixa.runway_meses !== null ? `${caixa.runway_meses.toLocaleString('pt-BR')} meses` : 'Gerando caixa',
      sub: caixa.burn_mensal > 0
        ? `burn médio 6m: ${fmtBRL(caixa.burn_mensal)}/mês`
        : `fluxo líquido médio 6m: ${fmtBRL(caixa.liquido_medio_6m)}/mês`,
      icone: 'fa-gauge-high', cor: caixa.runway_meses !== null && caixa.runway_meses < 12 ? RED : NAVY,
      info: 'Por quantos meses o caixa atual sustenta a operação, na média de queima (burn) dos últimos 6 meses. É a métrica nº 1 para fundos de venture capital. "Gerando caixa" = está entrando mais do que saindo.',
    },
    {
      titulo: 'DSO / Inadimplência',
      valor: recebiveis.dso_dias !== null ? `${recebiveis.dso_dias} dias` : '—',
      sub: `vencido: ${fmtBRL(recebiveis.vencido_total)} · em aberto: ${fmtBRL(recebiveis.aberto_total)}`,
      icone: 'fa-hourglass-half', cor: recebiveis.vencido_total > 0 ? '#e67e22' : GREEN,
      info: 'DSO = prazo médio de recebimento: quantos dias, em média, a receita fica "na rua" antes de virar dinheiro no caixa (saldo em aberto ÷ receita 12m × 365). Quanto menor, melhor.',
    },
  ];

  return (
    <div className="das-page fmp-dash-page ia-off">
      <div className="container-fluid mt-4">

        {/* ========================================
            HEADER FMP (hero)
        ======================================== */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card fmp-dash-hero">
              <div className="fmp-dash-grid" aria-hidden="true"></div>
              <div className="fmp-dash-aurora" aria-hidden="true">
                <span className="blob b1"></span>
                <span className="blob b2"></span>
                <span className="blob b3"></span>
              </div>
              <div className="card-body">
                {/* Topo: marca */}
                <div className="fmp-dash-top">
                  <div className="fmp-dash-brand">
                    <img className="fmp-dash-brand-logo" src={logoComece} alt="Comece" />
                    <span className="fmp-dash-brand-sep" aria-hidden="true"></span>
                    <span className="fmp-dash-brand-tag">FMP</span>
                  </div>
                  <div className="fmp-dash-topright">
                    <span className="fmp-dash-plan demo">
                      <i className="fas fa-flask me-1"></i>Página em teste
                    </span>
                  </div>
                </div>

                {/* Grade: info + receita consolidada */}
                <div className="fmp-dash-head">
                  <div>
                    <span className="fmp-dash-eyebrow">Relatório para Investidores</span>
                    <h1 className="fmp-dash-title">
                      {cliente?.nome || `Cliente ${clienteId}`}{' '}
                      <span className="grad">— métricas no padrão dos fundos</span>
                    </h1>
                    <div className="fmp-dash-metarow">
                      {cliente?.cnpj && <span><i className="fas fa-building"></i>CNPJ: {cliente.cnpj}</span>}
                      <span><i className="fas fa-database"></i>Fonte: 100% banco de dados</span>
                      <span>
                        <i className="fas fa-file-invoice"></i>
                        Base de receita: {baseTitulos ? 'títulos emitidos' : 'NFS-e emitidas'}
                      </span>
                      <span><i className="fas fa-clock"></i>Gerado em {dados.gerado_em}</span>
                      <span>
                        <i className="fas fa-calendar"></i>
                        Janela 12m: {rotuloMes(receita.periodo_ltm.inicio)} a {rotuloMes(receita.periodo_ltm.fim)}
                      </span>
                    </div>
                  </div>

                  <div className="fmp-dash-consol">
                    <div className="ph">
                      <span className="t">Receita · últimos 12 meses</span>
                      {receita.yoy_ltm !== null && (
                        <span className={`pill ${receita.yoy_ltm >= 0 ? 'green' : 'red'}`}>
                          {receita.yoy_ltm >= 0 ? '▲ +' : '▼ '}
                          {Math.abs(receita.yoy_ltm).toLocaleString('pt-BR')}% vs ano anterior
                        </span>
                      )}
                    </div>
                    <div className="big">{fmtBRL(receita.ltm)}</div>
                    <div className="sub">
                      EBITDA 12m {fmtBRL(ebitda.ltm)}
                      {ebitda.margem_ltm !== null && <> ({ebitda.margem_ltm.toLocaleString('pt-BR')}%)</>}
                      {' · '}
                      {caixa.runway_meses !== null
                        ? `runway ${caixa.runway_meses.toLocaleString('pt-BR')} meses`
                        : 'operação gerando caixa'}
                    </div>
                  </div>
                </div>

                {/* Botões de ação */}
                <div className="fmp-dash-mods">
                  <button type="button" className="fmp-dash-mod" onClick={() => navigate('/home')} title="Voltar ao início">
                    <i className="fas fa-arrow-left"></i>Voltar
                  </button>
                  <button type="button" className="fmp-dash-mod" onClick={carregar} title="Recalcular métricas">
                    <i className="fas fa-rotate"></i>Atualizar
                  </button>
                  <button
                    type="button"
                    className="fmp-dash-mod"
                    onClick={() => setAnonimizar(!anonimizar)}
                    title="Troca os nomes de clientes e fornecedores por Cliente/Fornecedor A, B, C (confidencialidade e LGPD)"
                    style={anonimizar ? { outline: `2px solid ${CYAN}` } : undefined}
                  >
                    <i className={`fas ${anonimizar ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    {anonimizar ? 'Clientes anonimizados' : 'Anonimizar clientes'}
                  </button>
                  <button type="button" className="fmp-dash-mod" disabled
                    title="Próxima etapa: exportação do relatório em PDF" style={{ opacity: 0.55, cursor: 'not-allowed' }}>
                    <i className="fas fa-download"></i>Baixar relatório (em breve)
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Highlights */}
        <div className="row g-3 mb-4">
          {highlights.map((h) => (
            <div className="col-md-4 col-xl-2" key={h.titulo}>
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body py-3">
                  <div className="d-flex align-items-center mb-1">
                    <i className={`fas ${h.icone} me-2`} style={{ color: h.cor }}></i>
                    <h6 className="card-title text-muted mb-0" style={{ fontSize: 12, minHeight: 30 }}>
                      {h.titulo}{h.info && <Info texto={h.info} />}
                    </h6>
                  </div>
                  <div className="d-flex align-items-center flex-wrap">
                    <h4 className="mb-0" style={{ color: h.cor, fontWeight: 700 }}>{h.valor}</h4>
                    {h.extra}
                  </div>
                  <small className="text-muted" style={{ fontSize: 11 }}>{h.sub}</small>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 1. Crescimento */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white">
                <h5 className="card-title mb-0">
                  <i className="fas fa-arrow-trend-up me-2" style={{ color: NAVY }}></i>
                  Crescimento — Receita mensal ({baseTitulos ? 'títulos emitidos' : 'NFS-e emitidas'}, últimos 26 meses)
                  <Info texto="Receita mês a mês pelas notas fiscais de serviço emitidas. Barras azul-marinho = últimos 12 meses; cinza = período anterior, para comparar visualmente o tamanho de cada fase. Passe o mouse numa barra para ver quantas notas e clientes o mês teve." />
                </h5>
                <small className="text-muted">
                  Ticket médio 12m: <strong>{fmtBRL(receita.ticket_medio_ltm)}</strong> ·
                  {' '}{receita.notas_ltm} {labelUnidade} · {receita.clientes_ltm} clientes ativos no período
                </small>
              </div>
              <div className="card-body">
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={serieReceita} margin={{ top: 10, right: 16, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef0f3" />
                    <XAxis dataKey="rotulo" tick={{ fontSize: 13 }} interval={1} />
                    <YAxis tickFormatter={fmtCompacto} tick={{ fontSize: 13 }} />
                    <Tooltip
                      contentStyle={tooltipDark} itemStyle={tooltipItem} labelStyle={tooltipLabel}
                      formatter={(v, nome) => nome === 'Receita' ? [fmtBRL2(v), nome] : [v, nome]}
                      labelFormatter={(l, p) => {
                        const d = p && p[0] && p[0].payload;
                        return d ? `${l} — ${d.notas} ${labelUnidade}, ${d.clientes} cliente(s)` : l;
                      }}
                    />
                    <Bar dataKey="total" name="Receita" radius={[3, 3, 0, 0]}>
                      {serieReceita.map((x) => (
                        <Cell key={x.mes} fill={x.mes >= receita.periodo_ltm.inicio ? NAVY : GRAY} />
                      ))}
                    </Bar>
                  </ComposedChart>
                </ResponsiveContainer>
                <small className="text-muted">Barras azul-marinho = últimos 12 meses (LTM); cinza = período anterior.</small>
              </div>
            </div>
          </div>
        </div>

        {/* 2. Rentabilidade */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white">
                <h5 className="card-title mb-0">
                  <i className="fas fa-scale-balanced me-2" style={{ color: GOLD }}></i>
                  Rentabilidade — Receita × EBITDA gerencial (13 meses)
                  <Info texto="Receita e resultado operacional (EBITDA) de cada mês. Barra ciano = mês com lucro operacional; vermelha = prejuízo no mês — normal em negócio por projeto, onde a receita se concentra em poucos meses e as despesas são contínuas." />
                </h5>
                <small className="text-muted">
                  EBITDA = receita ({baseTitulos ? 'títulos' : 'NFS-e'}) − impostos − custos − despesas operacionais (regime gerencial, sem despesas financeiras)
                </small>
              </div>
              <div className="card-body">
                {baseTitulos && (
                  <div className="alert alert-info py-2 mb-3">
                    <small>
                      <i className="fas fa-circle-info me-1"></i>
                      Este cliente não emite NFS-e para toda a receita, então o painel usa automaticamente
                      os <strong>títulos emitidos</strong> como base de receita (recebido em 12m: {fmtBRL(receita.recebido_ltm)}).
                    </small>
                  </div>
                )}
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={serieEbitda} margin={{ top: 10, right: 16, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef0f3" />
                    <XAxis dataKey="rotulo" tick={{ fontSize: 13 }} interval={0} />
                    <YAxis tickFormatter={fmtCompacto} tick={{ fontSize: 13 }} />
                    <Tooltip contentStyle={tooltipDark} itemStyle={tooltipItem} labelStyle={tooltipLabel} formatter={(v, nome) => [fmtBRL2(v), nome]} />
                    <Legend wrapperStyle={{ fontSize: 15 }} payload={[
                      { value: labelReceita, type: 'square', color: NAVY },
                      { value: 'EBITDA positivo (mês com lucro)', type: 'square', color: CYAN },
                      { value: 'EBITDA negativo (mês no prejuízo)', type: 'square', color: RED },
                    ]} />
                    <ReferenceLine y={0} stroke="#adb5bd" />
                    <Bar dataKey="receita" name={labelReceita} fill={NAVY} radius={[3, 3, 0, 0]} />
                    <Bar dataKey="ebitda" name="EBITDA" radius={[3, 3, 0, 0]}>
                      {serieEbitda.map((x) => (
                        <Cell key={x.mes} fill={x.ebitda >= 0 ? CYAN : RED} />
                      ))}
                    </Bar>
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        <div className="row g-4 mb-4">
          {/* 3. Caixa */}
          <div className="col-lg-7">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white">
                <h5 className="card-title mb-0">
                  <i className="fas fa-vault me-2" style={{ color: GREEN }}></i>
                  Caixa — Fluxo mensal realizado (extrato bancário)
                  <Info texto="Movimento real de dinheiro: tudo que entrou e saiu das contas bancárias em cada mês, direto do extrato. A linha dourada é o resultado líquido (entradas − saídas). Transferências entre contas próprias aparecem no bruto mas se anulam no líquido." />
                </h5>
                <small className="text-muted">
                  Entradas × saídas de todas as contas (o líquido anula transferências internas)
                </small>
              </div>
              <div className="card-body">
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={serieFluxo} margin={{ top: 10, right: 16, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef0f3" />
                    <XAxis dataKey="rotulo" tick={{ fontSize: 13 }} />
                    <YAxis tickFormatter={fmtCompacto} tick={{ fontSize: 13 }} />
                    <Tooltip contentStyle={tooltipDark} itemStyle={tooltipItem} labelStyle={tooltipLabel}
                      formatter={(v, nome) => [fmtBRL2(nome === 'Saídas' ? -Math.abs(v) : v), nome]} />
                    <Legend wrapperStyle={{ fontSize: 15 }} />
                    <ReferenceLine y={0} stroke="#adb5bd" />
                    <Bar dataKey="entradas" name="Entradas" fill={CYAN} radius={[3, 3, 0, 0]} />
                    <Bar dataKey="saidasAbs" name="Saídas" fill={NAVY} radius={[3, 3, 0, 0]} />
                    <Line dataKey="liquido" name="Líquido" stroke={GOLD} strokeWidth={2.5} dot={{ r: 3 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Saldos por conta */}
          <div className="col-lg-5">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white">
                <h5 className="card-title mb-0">
                  <i className="fas fa-building-columns me-2" style={{ color: NAVY }}></i>
                  Posição de caixa por conta
                  <Info texto="Onde o dinheiro está agora: último saldo conciliado de cada conta bancária e aplicação, com a data do saldo. Abaixo, a média de queima (burn) e o runway calculado." />
                </h5>
                <small className="text-muted">último saldo conciliado do extrato</small>
              </div>
              <div className="card-body">
                {caixa.contas.map((c) => (
                  <div key={c.conta} className="d-flex justify-content-between align-items-center border rounded p-2 mb-2">
                    <div>
                      <strong style={{ fontSize: 14 }}>{c.conta}</strong>
                      <small className="text-muted d-block" style={{ fontSize: 11 }}>
                        em {String(c.data).slice(0, 10).split('-').reverse().join('/')}
                      </small>
                    </div>
                    <h6 className="mb-0" style={{ color: c.saldo >= 0 ? NAVY : RED }}>{fmtBRL2(c.saldo)}</h6>
                  </div>
                ))}
                <div className="d-flex justify-content-between align-items-center rounded p-2 mt-3"
                  style={{ background: NAVY, color: '#fff' }}>
                  <strong>Total</strong>
                  <h5 className="mb-0" style={{ color: '#fff' }}>{fmtBRL2(caixa.total)}</h5>
                </div>
                <small className="text-muted d-block mt-2">
                  {caixa.burn_mensal > 0
                    ? <>Burn médio (6m): <strong>{fmtBRL(caixa.burn_mensal)}/mês</strong> → runway de <strong>{caixa.runway_meses} meses</strong></>
                    : <>Operação gerando caixa nos últimos 6 meses (líquido médio {fmtBRL(caixa.liquido_medio_6m)}/mês)</>}
                </small>
              </div>
            </div>
          </div>
        </div>

        <div className="row g-4 mb-4">
          {/* 5. Evolução do caixa */}
          <div className="col-lg-7">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white">
                <h5 className="card-title mb-0">
                  <i className="fas fa-chart-area me-2" style={{ color: GREEN }}></i>
                  Evolução do caixa — realizado e projetado
                  <Info texto="Trajetória do caixa: soma dos saldos de todas as contas no fim de cada mês (verde). A linha dourada tracejada é a PROJEÇÃO dos próximos 6 meses considerando só o que já está lançado/contratado no sistema (títulos e previsões) — receitas futuras ainda não contratadas não aparecem." />
                </h5>
                <small className="text-muted">
                  verde = realizado · dourado tracejado = projeção com os lançamentos já contratados
                  {ultimaProjecao && (
                    <> · <strong>caixa projetado em {rotuloMes(ultimaProjecao.mes)}: {fmtBRL(ultimaProjecao.saldo_projetado)}</strong></>
                  )}
                </small>
              </div>
              <div className="card-body">
                {ultimaProjecao && ultimaProjecao.saldo_projetado < 0 && (
                  <div className="alert alert-warning py-2 mb-3">
                    <small>
                      <i className="fas fa-triangle-exclamation me-1"></i>
                      <strong>Ponto de atenção:</strong> mantido só o que está contratado, o caixa projetado fica
                      negativo ({fmtBRL(ultimaProjecao.saldo_projetado)} em {rotuloMes(ultimaProjecao.mes)}) —
                      novas receitas precisam entrar no período.
                    </small>
                  </div>
                )}
                <ResponsiveContainer width="100%" height={260}>
                  <ComposedChart data={serieEvolucaoCaixa} margin={{ top: 10, right: 16, left: 8, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gradCaixaInv" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={GREEN} stopOpacity={0.35} />
                        <stop offset="100%" stopColor={GREEN} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef0f3" />
                    <XAxis dataKey="rotulo" tick={{ fontSize: 13 }} />
                    <YAxis tickFormatter={fmtCompacto} tick={{ fontSize: 13 }} />
                    <Tooltip contentStyle={tooltipDark} itemStyle={tooltipItem} labelStyle={tooltipLabel}
                      formatter={(v, nome) => [fmtBRL2(v), nome]} />
                    <ReferenceLine y={0} stroke="#adb5bd" />
                    <Area dataKey="saldo" name="Caixa (realizado)" stroke={GREEN} strokeWidth={2.5}
                      fill="url(#gradCaixaInv)" dot={{ r: 3 }} />
                    <Line dataKey="projetado" name="Caixa projetado" stroke={GOLD} strokeWidth={2.5}
                      strokeDasharray="6 4" dot={{ r: 3 }} connectNulls={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* 6. Base de clientes + disciplina orçamentária */}
          <div className="col-lg-5">
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-white">
                <h5 className="card-title mb-0">
                  <i className="fas fa-user-check me-2" style={{ color: CYAN }}></i>
                  Base de clientes — receita 12 meses
                  <Info texto="De onde vem a receita: clientes recorrentes (já compravam antes da janela de 12 meses) versus clientes novos conquistados no período. Receita recorrente alta = previsibilidade; novos = capacidade de expandir a base." />
                </h5>
                <small className="text-muted">
                  <strong>{base_clientes?.pct_recorrente ?? '—'}%</strong> da receita vem de clientes recorrentes
                </small>
              </div>
              <div className="card-body">
                <div className="progress mb-3" style={{ height: 14, background: GRAY }}>
                  <div className="progress-bar" style={{ width: `${base_clientes?.pct_recorrente || 0}%`, background: NAVY }}
                    title="Clientes recorrentes"></div>
                  <div className="progress-bar" style={{ width: `${100 - (base_clientes?.pct_recorrente || 0)}%`, background: CYAN }}
                    title="Clientes novos"></div>
                </div>
                <div className="row g-2">
                  <div className="col-6">
                    <div className="border rounded p-2 text-center">
                      <small className="text-muted d-block">Recorrentes</small>
                      <strong style={{ color: NAVY }}>{baseDet.recorrentes?.clientes || 0} cliente(s)</strong>
                      <small className="d-block">{fmtBRL(baseDet.recorrentes?.total || 0)}</small>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="border rounded p-2 text-center">
                      <small className="text-muted d-block">Novos no período</small>
                      <strong style={{ color: CYAN }}>{baseDet.novos?.clientes || 0} cliente(s)</strong>
                      <small className="d-block">{fmtBRL(baseDet.novos?.total || 0)}</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {orcamento && (
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white">
                  <h5 className="card-title mb-0">
                    <i className="fas fa-bullseye me-2" style={{ color: GOLD }}></i>
                    Disciplina orçamentária — {new Date().getFullYear()} YTD
                    <Info texto="Compara as despesas realizadas no ano com o orçamento aprovado para o mesmo período. Abaixo de 100% = gastou menos que o planejado. Mostra ao investidor que a empresa planeja e cumpre o orçamento." />
                  </h5>
                  <small className="text-muted">despesas realizadas × orçamento aprovado</small>
                </div>
                <div className="card-body">
                  {orcamento.orcado_ytd > 0 && (
                    <>
                      <div className="d-flex justify-content-between mb-1" style={{ fontSize: 14 }}>
                        <span><strong>Despesas</strong> — realizado: <strong>{fmtBRL(orcamento.realizado_ytd)}</strong></span>
                        <span className="text-muted">orçado: {fmtBRL(orcamento.orcado_ytd)}</span>
                      </div>
                      <div className="progress" style={{ height: 12, background: GRAY }}>
                        <div className="progress-bar" style={{
                          width: `${Math.min(orcamento.aderencia_pct, 100)}%`,
                          background: orcamento.aderencia_pct <= 100 ? GREEN : RED,
                        }}></div>
                      </div>
                      <small className="text-muted d-block mt-1 mb-3">
                        {orcamento.aderencia_pct <= 100
                          ? <>Executou <strong>{orcamento.aderencia_pct.toLocaleString('pt-BR')}%</strong> do orçamento de despesas — dentro do planejado</>
                          : <>Estourou o orçamento de despesas em <strong>{(orcamento.aderencia_pct - 100).toLocaleString('pt-BR')}%</strong></>}
                      </small>
                    </>
                  )}
                  {orcamento.receita_orcado_ytd > 0 && (
                    <>
                      <div className="d-flex justify-content-between mb-1" style={{ fontSize: 14 }}>
                        <span><strong>Receita</strong> — realizado: <strong>{fmtBRL(orcamento.receita_realizado_ytd)}</strong></span>
                        <span className="text-muted">meta: {fmtBRL(orcamento.receita_orcado_ytd)}</span>
                      </div>
                      <div className="progress" style={{ height: 12, background: GRAY }}>
                        <div className="progress-bar" style={{
                          width: `${Math.min(orcamento.receita_atingido_pct, 100)}%`,
                          background: orcamento.receita_atingido_pct >= 100 ? GREEN : NAVY,
                        }}></div>
                      </div>
                      <small className="text-muted d-block mt-1">
                        Atingiu <strong>{orcamento.receita_atingido_pct.toLocaleString('pt-BR')}%</strong> da meta de receita do período
                      </small>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="row g-4 mb-4">
          {/* 7. Composição de despesas */}
          <div className="col-lg-7">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white">
                <h5 className="card-title mb-0">
                  <i className="fas fa-layer-group me-2" style={{ color: NAVY }}></i>
                  Composição de despesas — 12 meses
                  <Info texto="Onde o dinheiro é gasto, agrupado pela estrutura do DRE (Pessoal, Custos dos serviços, Impostos...). O % ao lado mostra o peso de cada grupo sobre a receita — a soma é o inverso da margem EBITDA." />
                </h5>
                <small className="text-muted">
                  Total: <strong>{fmtBRL(despesas?.total_ltm)}</strong>
                  {despesas?.pct_receita !== null && <> · {despesas.pct_receita.toLocaleString('pt-BR')}% da receita</>}
                </small>
              </div>
              <div className="card-body">
                {(despesas?.grupos || []).filter((g) => g.grupo !== 'Investimentos').map((g, i) => (
                  <div key={g.grupo} className="mb-2">
                    <div className="d-flex justify-content-between" style={{ fontSize: 14 }}>
                      <span>{g.grupo}</span>
                      <span className="text-muted">
                        {fmtBRL(g.total)}{g.pct_receita !== null && <> · <strong>{g.pct_receita}% da receita</strong></>}
                      </span>
                    </div>
                    <div className="progress" style={{ height: 8, background: GRAY }}>
                      <div className="progress-bar" style={{
                        width: `${despesas.total_ltm ? Math.min(g.total / despesas.total_ltm * 100, 100) : 0}%`,
                        background: [NAVY, CYAN, GOLD, '#9db2c9', '#6c757d'][Math.min(i, 4)],
                      }}></div>
                    </div>
                  </div>
                ))}
                {(despesas?.investimentos_ltm || 0) > 0 && (
                  <div className="alert alert-light border py-2 mt-3 mb-0">
                    <small>
                      <i className="fas fa-seedling me-1" style={{ color: GREEN }}></i>
                      <strong>Investimentos no período (capex):</strong> {fmtBRL(despesas.investimentos_ltm)}
                      {receita.ltm > 0 && <> · {(despesas.investimentos_ltm / receita.ltm * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}% da receita</>}
                      {' '}— contabilizado fora do EBITDA (é aplicação de capital, não despesa da operação)
                    </small>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 8. Top fornecedores + DPO */}
          <div className="col-lg-5">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white">
                <h5 className="card-title mb-0">
                  <i className="fas fa-truck-field me-2" style={{ color: '#e67e22' }}></i>
                  Principais fornecedores — 12 meses
                  <Info texto="Maiores fornecedores pagos no período e o peso de cada um nas despesas (risco de dependência de fornecedor). DPO = prazo médio de pagamento: quantos dias a empresa leva para pagar seus compromissos — comparado ao DSO, revela o ciclo de caixa." />
                </h5>
                <small className="text-muted">
                  DPO (prazo médio de pagamento): <strong>{despesas?.dpo_dias !== null ? `${despesas.dpo_dias} dias` : '—'}</strong>
                </small>
              </div>
              <div className="card-body">
                {topFornecedores.map((f, i) => (
                  <div key={f.rotulo + i} className="d-flex justify-content-between align-items-center border-bottom py-2">
                    <span style={{ fontSize: 14 }}>{f.rotulo}</span>
                    <span style={{ fontSize: 14 }}>
                      <strong>{fmtBRL(f.total)}</strong> <span className="text-muted">({f.pct}%)</span>
                    </span>
                  </div>
                ))}
                <small className="text-muted d-block mt-2">
                  % sobre o total de despesas pagas no período
                </small>
              </div>
            </div>
          </div>
        </div>

        {/* 9. Unit Economics: LTV, retenção e churn */}
        {unit_economics && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white">
                  <h5 className="card-title mb-0">
                    <i className="fas fa-coins me-2" style={{ color: GOLD }}></i>
                    Unit Economics — LTV, retenção e churn (histórico completo)
                    <Info texto="Valor gerado por cliente: LTV = receita total que cada cliente trouxe ao longo do relacionamento; retenção/churn = quantos clientes permanecem ou saem a cada ano; NRR = se a receita dos clientes antigos cresceu (acima de 100%) ou encolheu. CAC (custo de conquistar um cliente) exige o investimento comercial informado." />
                  </h5>
                  <small className="text-muted">
                    Receita total por cliente ao longo do relacionamento (todas as notas emitidas desde o início da base)
                  </small>
                </div>
                <div className="card-body">
                  <div className="row g-2 mb-3">
                    <div className="col-md-3">
                      <div className="border rounded p-2 text-center h-100">
                        <small className="text-muted d-block">LTV médio (receita por cliente)</small>
                        <h5 className="mb-0" style={{ color: GOLD, fontWeight: 700 }}>{fmtBRL(unit_economics.ltv_medio)}</h5>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="border rounded p-2 text-center h-100">
                        <small className="text-muted d-block">Tempo médio de relacionamento</small>
                        <h5 className="mb-0" style={{ color: NAVY, fontWeight: 700 }}>
                          {(unit_economics.vida_media_meses / 12).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} anos
                        </h5>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="border rounded p-2 text-center h-100">
                        <small className="text-muted d-block">Clientes na base histórica</small>
                        <h5 className="mb-0" style={{ color: CYAN, fontWeight: 700 }}>{unit_economics.total_clientes_historico}</h5>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="border rounded p-2 text-center h-100">
                        <small className="text-muted d-block">CAC</small>
                        <h5 className="mb-0 text-muted" style={{ fontWeight: 700 }}>—</h5>
                        <small className="text-muted" style={{ fontSize: 10 }}>requer investimento comercial informado</small>
                      </div>
                    </div>
                  </div>

                  <div className="row g-4">
                    {/* Top clientes por LTV */}
                    <div className="col-lg-7">
                      <h6 style={{ color: '#212529' }}>Top clientes por valor histórico (LTV)</h6>
                      <div className="table-responsive">
                        <table className="table table-sm align-middle mb-0">
                          <thead>
                            <tr style={{ fontSize: 13 }}>
                              <th>Cliente</th><th>Desde</th><th>Vida</th>
                              <th className="text-end">Receita total</th><th className="text-end">Média/ano</th>
                            </tr>
                          </thead>
                          <tbody>
                            {ltvTop.map((c, i) => (
                              <tr key={c.rotulo + i} style={{ fontSize: 14 }}>
                                <td>{c.rotulo}</td>
                                <td><small>{c.desde.split('-').reverse().join('/').slice(3)}</small></td>
                                <td><small>{(c.vida_meses / 12).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} anos</small></td>
                                <td className="text-end"><strong>{fmtBRL(c.total)}</strong></td>
                                <td className="text-end">{fmtBRL(c.media_anual)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Retenção / churn / NRR por ano */}
                    <div className="col-lg-5">
                      <h6 style={{ color: '#212529' }}>Retenção de clientes e receita (NRR) por ano</h6>
                      <div className="table-responsive">
                        <table className="table table-sm align-middle mb-0">
                          <thead>
                            <tr style={{ fontSize: 13 }}>
                              <th>Ano</th><th className="text-end">Retenção</th>
                              <th className="text-end">Churn</th><th className="text-end">NRR</th>
                            </tr>
                          </thead>
                          <tbody>
                            {retencao.map((x) => (
                              <tr key={x.ano} style={{ fontSize: 14 }}>
                                <td>{x.ano}{x.parcial && <small className="text-muted"> (parcial)</small>}</td>
                                <td className="text-end">{x.retencao_clientes_pct.toLocaleString('pt-BR')}%</td>
                                <td className="text-end" style={{ color: x.churn_clientes_pct > 20 && !x.parcial ? RED : undefined }}>
                                  {x.churn_clientes_pct.toLocaleString('pt-BR')}%
                                </td>
                                <td className="text-end">
                                  <strong style={{ color: x.nrr_pct >= 100 ? GREEN : (x.parcial ? '#6c757d' : RED) }}>
                                    {x.nrr_pct !== null ? `${x.nrr_pct.toLocaleString('pt-BR')}%` : '—'}
                                  </strong>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <small className="text-muted d-block mt-2">
                        Retenção = % dos clientes do ano anterior que seguiram faturando ·
                        NRR = receita do ano dos clientes do ano anterior ÷ receita do ano anterior
                        (acima de 100% = os clientes antigos cresceram). Ano parcial ainda em andamento.
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 10. Projetos — só para clientes que controlam projetos no Omie */}
        {clienteControlaProjetos && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white">
                  <h5 className="card-title mb-0">
                    <i className="fas fa-diagram-project me-2" style={{ color: NAVY }}></i>
                    Projetos — receita, custo e margem (últimos 12 meses)
                    <Info texto="Resultado por projeto apurado do extrato bancário: tudo que entrou e saiu vinculado a cada código de projeto, com a margem resultante. A cobertura indica quanto das entradas do período tem projeto identificado." />
                  </h5>
                  <small className="text-muted">
                    Apurado do extrato bancário por projeto
                    {projetos.cobertura_receita_pct !== null &&
                      <> · {projetos.cobertura_receita_pct.toLocaleString('pt-BR')}% das entradas do período têm projeto vinculado</>}
                  </small>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-sm align-middle mb-0">
                      <thead>
                        <tr style={{ fontSize: 13 }}>
                          <th>Projeto</th>
                          <th className="text-end">Receita</th>
                          <th className="text-end">Custos</th>
                          <th className="text-end">Resultado</th>
                          <th style={{ width: 180 }}>Margem</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topProjetos.map((p) => (
                          <tr key={p.projeto} style={{ fontSize: 14 }}>
                            <td>{String(p.projeto).length > 48 ? String(p.projeto).slice(0, 48) + '…' : p.projeto}</td>
                            <td className="text-end"><strong>{fmtBRL(p.receita)}</strong></td>
                            <td className="text-end">{fmtBRL(p.despesa)}</td>
                            <td className="text-end" style={{ color: p.resultado >= 0 ? GREEN : RED }}>
                              {fmtBRL(p.resultado)}
                            </td>
                            <td>
                              {p.margem_pct !== null ? (
                                <div className="d-flex align-items-center gap-2">
                                  <div className="progress flex-grow-1" style={{ height: 8, background: GRAY }}>
                                    <div className="progress-bar" style={{
                                      width: `${Math.min(Math.max(p.margem_pct, 0), 100)}%`,
                                      background: p.margem_pct >= 50 ? GREEN : p.margem_pct >= 0 ? GOLD : RED,
                                    }}></div>
                                  </div>
                                  <small style={{ minWidth: 44, textAlign: 'right' }}>{p.margem_pct.toLocaleString('pt-BR')}%</small>
                                </div>
                              ) : <small className="text-muted">só custos</small>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="row g-4 mb-4">
          {/* 4. Concentração */}
          <div className="col-lg-7">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white">
                <h5 className="card-title mb-0">
                  <i className="fas fa-users me-2" style={{ color: CYAN }}></i>
                  Concentração de clientes — receita 12 meses
                  <Info texto="Quanto da receita depende de poucos clientes — um dos principais riscos que fundos avaliam. Top 1 e Top 5 mostram a fatia dos maiores. O botão 'Anonimizar' troca os nomes por Cliente A, B, C para compartilhar com confidencialidade." />
                </h5>
                <small className="text-muted">
                  Top 1: <strong>{concentracao.top1_pct}%</strong> · Top 5: <strong>{concentracao.top5_pct}%</strong> da receita
                </small>
              </div>
              <div className="card-body">
                {topClientes.map((c, i) => (
                  <div key={c.rotulo + i} className="mb-2">
                    <div className="d-flex justify-content-between" style={{ fontSize: 14 }}>
                      <span>{c.rotulo}</span>
                      <span className="text-muted">{fmtBRL(c.total)} · <strong>{c.pct}%</strong></span>
                    </div>
                    <div className="progress" style={{ height: 8, background: GRAY }}>
                      <div className="progress-bar" style={{
                        width: `${Math.min(c.pct, 100)}%`,
                        background: i === 0 ? NAVY : i < 5 ? CYAN : '#9db2c9',
                      }}></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Aging + falta faturar */}
          <div className="col-lg-5">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white">
                <h5 className="card-title mb-0">
                  <i className="fas fa-hourglass-half me-2" style={{ color: '#e67e22' }}></i>
                  Qualidade da receita — recebíveis em aberto
                  <Info texto="Títulos a receber que ainda não viraram caixa, classificados pelo tempo de atraso (aging). Pouco valor vencido + DSO baixo = receita de qualidade, que vira dinheiro rápido. 'Falta faturar' são ordens de serviço concluídas/abertas ainda sem nota emitida." />
                </h5>
                <small className="text-muted">
                  DSO: {recebiveis.dso_dias !== null ? `${recebiveis.dso_dias} dias` : '—'} (saldo em aberto ÷ receita 12m × 365)
                </small>
              </div>
              <div className="card-body">
                {faixasAging.map((f) => {
                  const v = aging[f.k];
                  return (
                    <div key={f.k} className="d-flex justify-content-between align-items-center border-bottom py-2">
                      <span style={{ fontSize: 14 }}>
                        <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: f.cor, marginRight: 8 }}></span>
                        {f.rotulo}
                      </span>
                      <span style={{ fontSize: 14 }}>
                        {v ? <><strong>{fmtBRL2(v.total)}</strong> <span className="text-muted">({v.qtd})</span></> : <span className="text-muted">—</span>}
                      </span>
                    </div>
                  );
                })}
                <div className="d-flex justify-content-between align-items-center py-2">
                  <strong style={{ fontSize: 14 }}>Total em aberto</strong>
                  <strong>{fmtBRL2(recebiveis.aberto_total)}</strong>
                </div>
                <div className="alert alert-light border py-2 mt-2 mb-0">
                  <small>
                    <i className="fas fa-file-circle-question me-1" style={{ color: NAVY }}></i>
                    Falta faturar (OS abertas): <strong>{falta_faturar.qtd}</strong>
                    {falta_faturar.qtd > 0 && <> · {fmtBRL2(falta_faturar.total)}</>}
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default RelatorioInvestidores;
